import { UploadOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Progress,
  Table,
  Upload,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import addIcon from "../../images/add-icon.svg";
import {
  api,
  isBlobUploadTimeoutError,
  uploadDirectToBlob,
} from "../../utils/api";
import { AppTypes } from "../../enum/app";
import { trimInput, trimStart, verifyFileHeader } from "../../utils/common";
import { DATE_FORMAT } from "../../utils/constants/common";
import { formatDate } from "../../utils/date";
import { isUnauthorized } from "../../utils/isUnauthorized";

const { TextArea } = Input;
const MAX_INSTALL_FILE_SIZE_BYTES = 300 * 1024 * 1024;
const INSTALL_FILE_TYPE_ERROR_MESSAGE =
  "このファイルはアップロードできません。\nファイル形式を確認してください。";

interface VersionItem {
  id: number;
  version: string;
  changes: string;
  releaseDate: string; // ISO string from backend
}

const pageSize = 10;

const AdminAppVersionsPage: React.FC = () => {
  const { id } = useParams();
  const installFileConfirmResolver = useRef<
    ((value: boolean | string) => void) | null
  >(null);

  const [data, setData] = useState<VersionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [appName, setAppName] = useState<string>("");
  const [appType, setAppType] = useState<AppTypes>(AppTypes.APP);
  const [uploadProgress, setUploadProgress] = useState(0);
  const installFileUploadNote =
    appType === AppTypes.PLUGIN
      ? "※プラグインをアップロードする場合は、.zip 形式のファイルを選択してください。"
      : "※アプリをアップロードする場合は、.exe 形式のファイルを選択してください。";
  const installFileAccept = appType === AppTypes.PLUGIN ? ".zip" : ".exe";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/admin/apps/${id}/versions`, {
          params: { page, pageSize },
        });
        if (!alive) return;
        setData(res.data.items);
        setTotal(res.data.total);
      } catch (e) {
        setData([]);
        setTotal(0);
      }
    })().catch(() => {
      setData([]);
      setTotal(0);
    });
    return () => {
      alive = false;
    };
  }, [id, page]);

  // Fetch app name to build folder prefix like admin-apps/<slug>/installer/<version>
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/admin/apps/${id}`);
        if (!alive) return;
        setAppName(res.data?.name || "");
        setAppType(res.data?.type || AppTypes.APP);
      } catch {
        setAppName("");
        setAppType(AppTypes.APP);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const items = data;

  const columns: ColumnsType<VersionItem> = [
    { title: "バージョン", dataIndex: "version", key: "version", width: 149 },
    {
      title: "日付",
      dataIndex: "releaseDate",
      key: "releaseDate",
      render: (v: string) => formatDate(v, DATE_FORMAT.SLASH_DATE),
      width: 149,
    },
    { title: "変更内容", dataIndex: "changes", key: "changes", width: 518 },
    {
      title: "アクション",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <div className="flex gap-2">
          <Popconfirm
            title="このバージョンを削除しますか？"
            description={`v${record.version} を削除すると復元できません。よろしいですか？`}
            okText="削除"
            okButtonProps={{ danger: true }}
            cancelText="キャンセル"
            onConfirm={async () => {
              try {
                await api.delete(`/api/admin/apps/${id}/versions/${record.id}`);
                message.success("バージョンを削除しました。");
                const res = await api.get(`/api/admin/apps/${id}/versions`, {
                  params: { page, pageSize },
                });
                setData(res.data.items);
                setTotal(res.data.total);
              } catch (e) {
                if (!isUnauthorized(e)) {
                  message.error("削除に失敗しました。");
                }
              }
            }}
          >
            <Button danger>削除</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Modal state
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [installFile, setInstallFile] = useState<File | null>(null);
  const [pendingInstallFile, setPendingInstallFile] = useState<File | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [isInstallFileConfirmOpen, setIsInstallFileConfirmOpen] =
    useState(false);
  const [isInstallFileTypeAlertOpen, setIsInstallFileTypeAlertOpen] =
    useState(false);

  const versionValue = Form.useWatch("version", form);
  const changesValue = Form.useWatch("changes", form);
  useEffect(() => {}, [versionValue]);

  const isValid = () => {
    return !!(versionValue && changesValue && installFile);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      if (!installFile) return;
      setSaving(true);
      setUploadProgress(0);
      // Build per-app slugged prefix and include version subfolder
      const slug = String(appName || "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      const basePrefix = slug ? `admin-apps/${slug}` : "admin-apps/unknown-app";
      // Upload installer directly to Blob Storage under installer/<version>
      const direct = await uploadDirectToBlob(
        installFile,
        `${basePrefix}/installer/${values.version}`,
        {
          onProgress: (ev) => {
            const pct = Math.round(
              (ev.loadedBytes / (installFile.size || 1)) * 100,
            );
            setUploadProgress(pct);
          },
          blockSizeMB: 8,
          concurrency: 8,
        },
      );
      const installUrl = direct.url;
      await api.post(`/api/admin/apps/${id}/versions`, {
        version: values.version,
        changes: values.changes,
        installerUrl: installUrl,
        installerFileName: direct.installerFileName,
      });
      message.success("バージョンを更新しました。");
      setOpen(false);
      form.resetFields();
      setInstallFile(null);
      setUploadProgress(0);
      // refresh list
      const res = await api.get(`/api/admin/apps/${id}/versions`, {
        params: { page, pageSize },
      });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      if (!isUnauthorized(e)) {
        if (isBlobUploadTimeoutError(e)) {
          message.error(
            e.code === "UPLOAD_INACTIVITY_TIMEOUT"
              ? "アップロードが120秒間停止したため、タイムアウトしました。"
              : "アップロードが10分を超えたため、タイムアウトしました。",
          );
          return;
        }
        // validation errors are handled by antd; other errors show message
        if ((e as any).errorFields != null) {
          return;
        }
        if ((e as any)?.response?.status === 409) {
          message.error(
            "バージョン名が既に登録されています。別のバージョン名を入力してください。",
          );
          return;
        }
        message.error("バージョン更新に失敗しました。");
      }
    } finally {
      setSaving(false);
    }
  };

  const openModal = () => {
    form.resetFields();
    setInstallFile(null);
    setPendingInstallFile(null);
    setIsInstallFileConfirmOpen(false);
    setIsInstallFileTypeAlertOpen(false);
    setUploadProgress(0);
    setOpen(true);
  };

  const handleInstallFileConfirmOk = () => {
    if (pendingInstallFile) {
      setInstallFile(pendingInstallFile);
    }
    installFileConfirmResolver.current?.(false);
    installFileConfirmResolver.current = null;
    setPendingInstallFile(null);
    setIsInstallFileConfirmOpen(false);
  };

  const handleInstallFileConfirmCancel = () => {
    installFileConfirmResolver.current?.(Upload.LIST_IGNORE);
    installFileConfirmResolver.current = null;
    setPendingInstallFile(null);
    setIsInstallFileConfirmOpen(false);
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <div>
        <h2 className="text-lg font-normal mb-2 text-start">バージョン一覧</h2>
        <Breadcrumb
          items={[
            { title: <Link to="/admin">アプリ一覧</Link> },
            { title: "バージョン一覧" },
          ]}
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-[#EBEEF2] custom-table">
        <Table
          rowKey="id"
          dataSource={items}
          columns={columns}
          pagination={false}
        />
      </div>

      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-600">全 {total}件</div>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
          />
        </div>
      </div>

      <div className="flex justify-start">
        <Button
          className="base-button w-[156px] h-[41px]"
          onClick={() => openModal()}
        >
          <img src={addIcon} alt="アプリ登録アイコン" className="h-6 w-6" />
          <span>バージョン更新</span>
        </Button>
      </div>

      <Modal
        title="バージョンの更新"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnHidden
        className="custom-modal-version"
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <div className="flex flex-col gap-4">
            <Form.Item
              label={
                <span className="font-medium">
                  バージョン <span className="text-red-500">※</span>
                </span>
              }
              name="version"
              rules={[
                { required: true, message: "バージョンを入力してください" },
                {
                  pattern: /^\d+\.\d+\.\d+$/,
                  message:
                    "バージョンは数字の形式 x.y.z で入力してください（例: 1.2.3）",
                },
              ]}
            >
              <Input
                maxLength={255}
                className="base-input h-[46px]"
                placeholder="例) 1.2.3"
                onChange={(e) => {
                  form.setFieldValue("version", trimStart(e.target.value));
                }}
                onBlur={(e) => {
                  form.setFieldValue("version", trimInput(e.target.value));
                }}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="font-medium">
                  変更内容 <span className="text-red-500">※</span>
                </span>
              }
              name="changes"
              rules={[{ required: true, message: "" }]}
            >
              <TextArea
                className="base-input h-[46px]"
                rows={5}
                placeholder="変更点を入力"
                onChange={(e) => {
                  form.setFieldValue("changes", trimStart(e.target.value));
                }}
                onBlur={(e) => {
                  form.setFieldValue("changes", trimInput(e.target.value));
                }}
                maxLength={999}
              />
            </Form.Item>
            <Form.Item
              label={
                <span className="font-medium">
                  インストールファイルのアップロード
                  <span className="text-red-500">※</span>
                </span>
              }
            >
              <div className="upload-container">
                <Upload
                  key={appType}
                  beforeUpload={async (file: File) => {
                    const isOversized = file.size > MAX_INSTALL_FILE_SIZE_BYTES;
                    const fileName = file.name.toLowerCase();
                    const isZip = fileName.endsWith(".zip");
                    const isExe = fileName.endsWith(".exe");
                    const isExpectedFile =
                      appType === AppTypes.PLUGIN ? isZip : isExe;

                    if (!isExpectedFile) {
                      setIsInstallFileTypeAlertOpen(true);
                      return Upload.LIST_IGNORE;
                    }

                    if (isZip) {
                      const isRealFile = await verifyFileHeader(file);
                      if (!isRealFile) {
                        message.error("ファイルが破損しているか、無効な形式です。");
                        return Upload.LIST_IGNORE;
                      }
                    }
                    if (isOversized) {
                      setPendingInstallFile(file);
                      setIsInstallFileConfirmOpen(true);
                      return new Promise((resolve) => {
                        installFileConfirmResolver.current = resolve;
                      });
                    }
                    setInstallFile(file);
                    return false;
                  }}
                  onRemove={() => {
                    setInstallFile(null);
                  }}
                  maxCount={1}
                  showUploadList={
                    installFile ? { showPreviewIcon: false } : false
                  }
                  accept={installFileAccept}
                >
                  <Button
                    icon={<UploadOutlined />}
                    className="base-upload w-full h-[46px] rounded-[6px] border px-[120px]"
                  >
                    アップロード
                  </Button>
                </Upload>
                <div className="mt-2 w-full text-left text-xs">
                  {installFileUploadNote}
                </div>
              </div>
              {installFile && (
                <div className="mt-2 text-sm text-gray-600">
                  {installFile.name}
                </div>
              )}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-2">
                  <Progress
                    percent={uploadProgress}
                    size="small"
                    className="w-full max-w-[378px]"
                  />
                </div>
              )}
            </Form.Item>

            <div className="flex justify-end gap-[10px]">
              <Button
                className="base-button-outline h-[42px] w-[120px]"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                キャンセル
              </Button>
              <Button
                className="base-button h-[42px] w-[120px] tracking-[-1px]"
                onClick={onSave}
                loading={saving}
                disabled={!isValid()}
              >
                {saving && uploadProgress > 0 && uploadProgress < 100
                  ? `アップロード中… ${uploadProgress}%`
                  : "保存"}
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      <Modal
        title="ファイルサイズを確認してください"
        open={isInstallFileConfirmOpen}
        onOk={handleInstallFileConfirmOk}
        onCancel={handleInstallFileConfirmCancel}
        okText="はい"
        cancelText="いいえ"
        closable={false}
        maskClosable={false}
        centered
      >
        <div>ファイルサイズが300MBを超えていますが、このまま登録しますか？</div>
      </Modal>

      <Modal
        open={isInstallFileTypeAlertOpen}
        closable={false}
        maskClosable={false}
        centered
        footer={null}
        className="install-file-type-alert-modal"
      >
        <div className="flex h-full flex-col items-center justify-center">
          <div className="whitespace-pre-line text-center text-base leading-6 text-[#1F2A44]">
            {INSTALL_FILE_TYPE_ERROR_MESSAGE}
          </div>
          <div className="mt-4 flex justify-center">
            <Button
              className="base-button h-[32px] w-[120px]"
              onClick={() => setIsInstallFileTypeAlertOpen(false)}
            >
              はい
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminAppVersionsPage;
